<template>
    <div class="ix-dm" :class="{ 'is-dragging': isDragging && !dropFolderId, 'is-min': display == 'min' }"
        @dragenter="onDragEnter" @dragover="onDragOver" @dragleave="onDragLeave" @drop="onDrop">

        <div class="ix-dm-toolbar">
            <div class="ix-dm-breadcrumb">
                <a @click="changeNode(null)" class="ix-dm-crumb" title="Racine"><i class="far fa-home-lg-alt"></i></a>
                <template v-if="node">
                    <span v-for="item in breadcrumbs" :key="'crumb-' + item.id" class="ix-dm-crumb-item">
                        <i class="fas fa-chevron-right ix-dm-sep"></i>
                        <a @click="changeNode(item.id)" class="ix-dm-crumb">{{ item.name }}</a>
                    </span>
                    <i class="fas fa-chevron-right ix-dm-sep"></i>
                    <span class="ix-dm-crumb current">{{ node.name }}</span>
                </template>
                <template v-else>
                    <i class="fas fa-chevron-right ix-dm-sep"></i>
                    <span class="ix-dm-crumb current">Documents</span>
                </template>
                <span class="ix-dm-count is-uploading" v-if="uploadingActive">
                    · <i class="far fa-circle-notch fa-spin"></i> Envoi de {{ activeUploadsCount }} fichier{{ activeUploadsCount > 1 ? 's' : '' }}… {{ uploadsProgress }} %
                </span>
                <span class="ix-dm-count is-success" v-else-if="justAdded > 0">
                    · <i class="fas fa-check-circle"></i> {{ justAdded }} fichier{{ justAdded > 1 ? 's' : '' }} ajouté{{ justAdded > 1 ? 's' : '' }}
                </span>
                <span class="ix-dm-count" v-else-if="loading"><i class="far fa-circle-notch fa-spin"></i></span>
                <span class="ix-dm-count" v-else-if="rowsCount > 0">· {{ rowsCount }} élément{{ rowsCount > 1 ? 's' : '' }}</span>
            </div>

            <div class="ix-dm-search" v-if="display != 'min' && rowsCount > 0">
                <i class="far fa-search"></i>
                <input type="text" v-model="search" placeholder="Rechercher un document">
            </div>

            <template v-if="canEdit">
                <button type="button" class="ix-dm-btn" @click="openFolderModal()" :title="display == 'min' ? 'Nouveau dossier' : null">
                    <i class="far fa-folder-plus"></i><span v-if="display != 'min'"> Nouveau dossier</span>
                </button>
                <button type="button" class="ix-dm-btn primary" @click="browseFiles()" v-if="rowsCount > 0 || loading" :title="display == 'min' ? 'Ajouter des fichiers' : null">
                    <i class="far fa-cloud-upload"></i><span v-if="display != 'min'"> Ajouter des fichiers</span>
                </button>
                <input type="file" multiple class="d-none" ref="fileInput" @change="onFilesPicked">
            </template>
            <div class="ix-dm-global-progress" v-if="uploadingActive"><span :style="{ width: uploadsProgress + '%' }"></span></div>
        </div>

        <table class="ix-dm-table" v-if="rowsCount > 0 || uploads.length > 0">
            <thead>
                <tr>
                    <th class="ix-dm-sortable" @click="changeOrder('name')">
                        Nom
                        <i class="fas fa-caret-down" v-if="order == 'name'"></i>
                        <i class="fas fa-caret-up" v-if="order == '!name'"></i>
                    </th>
                    <th class="ix-dm-col-detail" v-if="linked">Origine</th>
                    <th class="ix-dm-sortable ix-dm-col-detail" @click="changeOrder('size')" v-if="display != 'min'">
                        Taille
                        <i class="fas fa-caret-down" v-if="order == 'size'"></i>
                        <i class="fas fa-caret-up" v-if="order == '!size'"></i>
                    </th>
                    <th class="ix-dm-sortable ix-dm-col-detail" @click="changeOrder('updated_at')" v-if="display != 'min'">
                        Modifié
                        <i class="fas fa-caret-down" v-if="order == 'updated_at'"></i>
                        <i class="fas fa-caret-up" v-if="order == '!updated_at'"></i>
                    </th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <!-- Fichiers en cours d'envoi : en tête de liste, avec leur progression -->
                <tr v-for="item in uploads" :key="'upload-' + item.key" class="ix-dm-upload-item" :class="'is-' + item.status">
                    <td>
                        <div class="ix-dm-name">
                            <div class="ix-dm-ico" :class="item.status == 'done' ? 'done' : fileType(item.extension)">
                                <i class="fas fa-check" v-if="item.status == 'done'"></i>
                                <i class="far" :class="fileIcon(item.extension)" v-else></i>
                            </div>
                            <div class="ix-dm-name-body ix-dm-upload-body">
                                <span class="ix-dm-name-text">{{ item.name }}.{{ item.extension }}</span>
                                <div class="ix-dm-upload-bar" v-if="['pending', 'uploading', 'processing'].includes(item.status)">
                                    <span :class="{ 'is-processing': item.status == 'processing' }" :style="{ width: (item.status == 'processing' ? 100 : item.progress) + '%' }"></span>
                                </div>
                                <div class="ix-dm-name-meta">
                                    <template v-if="item.status == 'pending'">En attente…</template>
                                    <template v-else-if="item.status == 'uploading'">Envoi… {{ item.progress }} % · {{ item.loaded | formatFileSize(1) }} sur {{ item.size | formatFileSize(1) }}</template>
                                    <template v-else-if="item.status == 'processing'">Finalisation…</template>
                                    <span v-else-if="item.status == 'done'" class="text-success">Ajouté</span>
                                    <span v-else-if="item.status == 'error'" class="text-danger"><i class="fas fa-exclamation-circle"></i> {{ item.error }}</span>
                                    <span v-if="item.folder"> · dans « {{ item.folder.name }} »</span>
                                </div>
                            </div>
                        </div>
                    </td>
                    <td class="ix-dm-col-detail" v-if="linked"><span class="ix-dm-origin">{{ originLabel }}</span></td>
                    <td class="ix-dm-muted ix-dm-col-detail" v-if="display != 'min'">{{ item.size | formatFileSize }}</td>
                    <td class="ix-dm-muted ix-dm-col-detail" v-if="display != 'min'">—</td>
                    <td class="text-right">
                        <div class="ix-dm-actions show">
                            <template v-if="item.status == 'error'">
                                <button type="button" class="ix-dm-act" @click="retryUpload(item)" title="Réessayer"><i class="far fa-redo"></i></button>
                                <button type="button" class="ix-dm-act" @click="removeUpload(item)" title="Retirer"><i class="far fa-times"></i></button>
                            </template>
                            <button type="button" class="ix-dm-act" v-else-if="item.status == 'pending' || item.status == 'uploading'" @click="cancelUpload(item)" title="Annuler l'envoi"><i class="far fa-times"></i></button>
                        </div>
                    </td>
                </tr>

                <tr v-for="folder in filteredFolders" :key="'folder-' + folder.id" :data-folder-id="folder.id"
                    :class="{ 'is-drop-target': dropFolderId == folder.id }">
                    <td>
                        <div class="ix-dm-name">
                            <div class="ix-dm-ico folder"><i class="fas" :class="dropFolderId == folder.id ? 'fa-folder-open' : 'fa-folder'"></i></div>
                            <div class="ix-dm-name-body">
                                <a @click="changeNode(folder.id)" class="ix-dm-name-text">{{ folder.name }}</a>
                                <div class="ix-dm-drop-hint" v-if="dropFolderId == folder.id"><i class="far fa-arrow-down"></i> Déposer dans ce dossier</div>
                                <div class="ix-dm-name-meta" v-else-if="folder.childs_count !== undefined">
                                    {{ folder.childs_count }} élément{{ folder.childs_count > 1 ? 's' : '' }}<span class="ix-dm-mobile-only"> · {{ folder.updated_at | formatDate('DD/MM/YYYY') }}</span>
                                </div>
                            </div>
                        </div>
                    </td>
                    <td class="ix-dm-col-detail" v-if="linked"><span class="ix-dm-origin">{{ originLabel }}</span></td>
                    <td class="ix-dm-muted ix-dm-col-detail" v-if="display != 'min'">—</td>
                    <td class="ix-dm-muted ix-dm-col-detail" v-if="display != 'min'">{{ folder.updated_at | formatDate('DD/MM/YYYY') }}</td>
                    <td class="text-right">
                        <div class="ix-dm-actions">
                            <div class="btn-group" v-if="canEdit">
                                <button type="button" class="ix-dm-act" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-boundary="viewport">
                                    <i class="far fa-ellipsis-h"></i>
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <button class="ix-btn-dropdown-item" @click="changeNode(folder.id)"><i class="far fa-folder-open"></i> Ouvrir</button>
                                    <button class="ix-btn-dropdown-item" @click="editDocument(folder)"><i class="far fa-pen"></i> Modifier</button>
                                    <button class="ix-btn-dropdown-item danger" @click="deleteDocument(folder)"><i class="far fa-trash"></i> Supprimer</button>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>

                <tr v-for="document in filteredDocuments" :key="'document-' + document.id" :class="{ 'is-new': highlightIds.includes(document.id) }">
                    <td>
                        <div class="ix-dm-name">
                            <img v-if="isImage(document) && !brokenThumbs[document.id]" :src="document.document_path" loading="lazy" class="ix-dm-thumb" alt="" @click="preview(document)"
                                @error="$set(brokenThumbs, document.id, true)">
                            <div v-else class="ix-dm-ico" :class="fileType(document.extension)" @click="preview(document)"><i class="far" :class="fileIcon(document.extension)"></i></div>
                            <div class="ix-dm-name-body">
                                <a :href="document.document_path" class="ix-dm-name-text" @click.prevent="preview(document)">{{ document.name }}.{{ document.extension }}</a>
                                <div class="ix-dm-name-meta" v-if="document.user">Ajouté par {{ document.user.fullname }}</div>
                                <div class="ix-dm-name-meta ix-dm-mobile-only">{{ document.size | formatFileSize }} · {{ document.updated_at | formatDate('DD/MM/YYYY') }}</div>
                            </div>
                        </div>
                    </td>
                    <td class="ix-dm-col-detail" v-if="linked"><span class="ix-dm-origin">{{ originLabel }}</span></td>
                    <td class="ix-dm-muted ix-dm-col-detail" v-if="display != 'min'">{{ document.size | formatFileSize }}</td>
                    <td class="ix-dm-muted ix-dm-col-detail" v-if="display != 'min'">{{ document.updated_at | formatDate('DD/MM/YYYY') }}</td>
                    <td class="text-right">
                        <div class="ix-dm-actions">
                            <a :href="document.download_path || document.document_path" class="ix-dm-act" title="Télécharger"><i class="far fa-arrow-to-bottom"></i></a>
                            <div class="btn-group" v-if="canEdit">
                                <button type="button" class="ix-dm-act" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-boundary="viewport">
                                    <i class="far fa-ellipsis-h"></i>
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <button class="ix-btn-dropdown-item" @click="editDocument(document)"><i class="far fa-pen"></i> Modifier</button>
                                    <button class="ix-btn-dropdown-item danger" @click="deleteDocument(document)"><i class="far fa-trash"></i> Supprimer</button>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>

                <!--
                    Documents des éléments liés : seuls le téléchargement et
                    la suppression sont proposés (aucune modification ni
                    déplacement depuis cet élément). Affichés uniquement à la
                    racine, ils n'appartiennent à aucun dossier de l'élément
                    courant.
                -->
                <tr v-for="linkedDocument in filteredLinkedDocuments" :key="'linked-' + linkedDocument.id">
                    <td>
                        <div class="ix-dm-name">
                            <img v-if="isImage(linkedDocument) && !brokenThumbs['l' + linkedDocument.id]" :src="linkedDocument.document_path" loading="lazy" class="ix-dm-thumb" alt="" @click="preview(linkedDocument)"
                                @error="$set(brokenThumbs, 'l' + linkedDocument.id, true)">
                            <div v-else class="ix-dm-ico" :class="fileType(linkedDocument.extension)" @click="preview(linkedDocument)"><i class="far" :class="fileIcon(linkedDocument.extension)"></i></div>
                            <div class="ix-dm-name-body">
                                <a :href="linkedDocument.document_path" class="ix-dm-name-text" @click.prevent="preview(linkedDocument)">{{ linkedDocument.name }}.{{ linkedDocument.extension }}</a>
                                <div class="ix-dm-name-meta" v-if="linkedDocument.user">Ajouté par {{ linkedDocument.user.fullname }}</div>
                                <div class="ix-dm-name-meta ix-dm-mobile-only">
                                    <span v-if="linkedDocument.origin" class="ix-dm-origin linked"><i class="far fa-link"></i> {{ linkedDocument.origin.reference }}</span>
                                    {{ linkedDocument.size | formatFileSize }} · {{ linkedDocument.updated_at | formatDate('DD/MM/YYYY') }}
                                </div>
                            </div>
                        </div>
                    </td>
                    <td class="ix-dm-col-detail">
                        <template v-if="linkedDocument.origin">
                            <a v-if="linkedDocument.origin.url" :href="linkedDocument.origin.url" class="ix-dm-origin linked"><i class="far fa-link"></i> {{ linkedDocument.origin.reference }}</a>
                            <span v-else class="ix-dm-origin linked"><i class="far fa-link"></i> {{ linkedDocument.origin.reference }}</span>
                        </template>
                    </td>
                    <td class="ix-dm-muted ix-dm-col-detail" v-if="display != 'min'">{{ linkedDocument.size | formatFileSize }}</td>
                    <td class="ix-dm-muted ix-dm-col-detail" v-if="display != 'min'">{{ linkedDocument.updated_at | formatDate('DD/MM/YYYY') }}</td>
                    <td class="text-right">
                        <div class="ix-dm-actions">
                            <a :href="linkedDocument.download_path || linkedDocument.document_path" class="ix-dm-act" title="Télécharger"><i class="far fa-arrow-to-bottom"></i></a>
                            <div class="btn-group" v-if="canEdit">
                                <button type="button" class="ix-dm-act" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" data-boundary="viewport">
                                    <i class="far fa-ellipsis-h"></i>
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <button class="ix-btn-dropdown-item danger" @click="deleteLinkedDocument(linkedDocument)"><i class="far fa-trash"></i> Supprimer</button>
                                </div>
                            </div>
                        </div>
                    </td>
                </tr>

                <tr v-if="search && filteredCount == 0">
                    <td colspan="100%" class="text-center ix-dm-muted">Aucun document ne correspond à « {{ search }} »</td>
                </tr>
            </tbody>
        </table>

        <!-- Zone de dépôt -->
        <template v-if="canEdit && !loading">
            <div class="ix-dm-empty" v-if="rowsCount == 0 && uploads.length == 0" @click="browseFiles()">
                <div class="ix-dm-empty-circle"><i class="far fa-cloud-upload"></i></div>
                <div class="ix-text-bold"><span class="ix-dm-desktop-only">Glissez-déposez vos fichiers ici</span><span class="ix-dm-mobile-only">Aucun document</span></div>
                <div class="ix-dm-muted mb-3">PDF, images, Office… 10 Mo max par fichier</div>
                <button type="button" class="ix-dm-btn primary"><i class="far fa-folder-open"></i> <span class="ix-dm-desktop-only">Parcourir mes fichiers</span><span class="ix-dm-mobile-only">Ajouter des fichiers</span></button>
            </div>
            <div class="ix-dm-footer-drop" v-else-if="display != 'min'">
                <i class="far fa-cloud-upload mr-1"></i> Glissez-déposez des fichiers ici ou <a @click="browseFiles()">parcourez</a> · 10 Mo max par fichier
            </div>
        </template>
        <div class="ix-dm-empty-readonly" v-else-if="!canEdit && !loading && !loadingLinked && rowsCount == 0">
            Il n'y a pas encore de documents associés
        </div>

        <!-- Survol pendant le glisser-déposer -->
        <div class="ix-dm-overlay" v-if="isDragging && !dropFolderId">
            <div class="ix-dm-overlay-bubble">
                <i class="far fa-cloud-upload"></i>
                <div class="ix-text-bold mt-2">Déposez pour ajouter vos fichiers</div>
                <div class="ix-dm-muted">dans {{ node ? '« ' + node.name + ' »' : 'Documents' }}</div>
            </div>
        </div>

        <documents-manager-modal :url="url" :roles-url="rolesUrl" :admin="admin" ref="documentsManagerModal"
            :documentable-id="documentableId" :documentable-type="documentableType" :node="node"
            :breadcrumbs="breadcrumbs" @upload="startUploads" @saved="getData">
        </documents-manager-modal>

        <document-preview ref="preview" :url="url"></document-preview>
    </div>
</template>

<script>
import DocumentsManagerModal from "./DocumentsManagerModal"
import DocumentPreview from "./DocumentPreview"
import { fileType, fileIcon } from "./documentFileTypes"

export default {
    name: "DocumentsManager",
    components: {
        DocumentsManagerModal,
        DocumentPreview,
    },
    props: {
        documentableType: String,
        documentableId: Number,
        admin: Number,
        display: String,
        readonly: String,
        url: "",
        rolesUrl: "",
        // Active l'affichage des documents liés et la colonne « Origine ».
        // Désactivé par défaut : les emplacements qui ne la transmettent pas
        // sont inchangés.
        linked: { type: Boolean, default: false },
        // Libellé de la colonne « Origine » pour les documents propres (ex. « Ce devis »)
        originLabel: { type: String, default: 'Cet élément' },
    },
    watch: {
        // Quitter la page pendant un envoi l'interromprait : le navigateur demande confirmation
        uploadingActive(active) {
            if (active) {
                window.addEventListener('beforeunload', this.onBeforeUnload);
            } else {
                window.removeEventListener('beforeunload', this.onBeforeUnload);
            }
        },
        documentableId: async function () {
            if (this.documentableId) {
                await this.getData();
            }
        },
    },
    data() {
        return {
            nodeId: null,
            node: null,
            breadcrumbs: [],
            folders: [],
            documents: [],
            loading: true,
            order: 'name',
            linkedDocuments: [],
            loadingLinked: false,
            search: '',
            // Glisser-déposer : compteur pour ignorer les entrées / sorties des éléments enfants
            dragDepth: 0,
            isDragging: false,
            dropFolderId: null,
            uploads: [],
            uploadSeq: 0,
            justAdded: 0,
            highlightIds: [],
            brokenThumbs: {},
        }
    },
    computed: {
        canEdit() {
            return !this.readonly;
        },
        // Les documents liés ne sont affichés qu'à la racine de l'arborescence
        visibleLinkedDocuments() {
            return this.linked && !this.nodeId ? this.linkedDocuments : [];
        },
        rowsCount() {
            return this.folders.length + this.documents.length + this.visibleLinkedDocuments.length;
        },
        filteredFolders() {
            return this.folders.filter(this.matchesSearch);
        },
        filteredDocuments() {
            return this.documents.filter(this.matchesSearch);
        },
        filteredLinkedDocuments() {
            return this.visibleLinkedDocuments.filter(this.matchesSearch);
        },
        filteredCount() {
            return this.filteredFolders.length + this.filteredDocuments.length + this.filteredLinkedDocuments.length;
        },
        activeUploads() {
            return this.uploads.filter(item => ['pending', 'uploading', 'processing'].includes(item.status));
        },
        activeUploadsCount() {
            return this.activeUploads.length;
        },
        uploadingActive() {
            return this.activeUploadsCount > 0;
        },
        // Progression globale en octets, sur les fichiers de l'envoi en cours
        uploadsProgress() {
            const items = this.uploads.filter(item => item.status != 'error');
            const total = items.reduce((sum, item) => sum + item.size, 0);
            const loaded = items.reduce((sum, item) => sum + (['done', 'processing'].includes(item.status) ? item.size : item.loaded), 0);
            return total > 0 ? Math.min(100, Math.round(loaded / total * 100)) : 0;
        },
    },
    methods: {
        fileType,
        fileIcon,
        isImage(document) {
            return fileType(document.extension) == 'img';
        },
        matchesSearch(item) {
            if (!this.search) return true;
            const name = (item.name + (item.extension ? '.' + item.extension : '')).toLowerCase();
            return name.includes(this.search.toLowerCase());
        },
        // Visionneuse : navigation entre tous les fichiers affichés (propres et liés)
        preview(document) {
            this.$refs.preview.open(document, this.filteredDocuments.concat(this.filteredLinkedDocuments));
        },
        openFolderModal() {
            this.$refs.documentsManagerModal.startDocumentModal(true);
        },
        editDocument(document) {
            this.$refs.documentsManagerModal.editDocument(document);
        },
        browseFiles() {
            if (this.$refs.fileInput) {
                this.$refs.fileInput.click();
            }
        },
        onFilesPicked(e) {
            const files = Array.from(e.target.files || []);
            e.target.value = '';
            if (files.length) {
                this.$refs.documentsManagerModal.startUpload(files, null);
            }
        },

        /* Glisser-déposer de fichiers depuis l'ordinateur */
        hasFiles(e) {
            return this.canEdit && e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');
        },
        onDragEnter(e) {
            if (!this.hasFiles(e)) return;
            e.preventDefault();
            this.dragDepth++;
            this.isDragging = true;
        },
        onDragOver(e) {
            if (!this.hasFiles(e)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            const row = e.target.closest ? e.target.closest('[data-folder-id]') : null;
            this.dropFolderId = row ? parseInt(row.dataset.folderId) : null;
        },
        onDragLeave(e) {
            if (!this.hasFiles(e)) return;
            this.dragDepth = Math.max(0, this.dragDepth - 1);
            if (this.dragDepth == 0) {
                this.isDragging = false;
                this.dropFolderId = null;
            }
        },
        onDrop(e) {
            if (!this.hasFiles(e)) return;
            e.preventDefault();
            const folder = this.dropFolderId ? this.folders.find(item => item.id == this.dropFolderId) : null;
            const files = Array.from(e.dataTransfer.files || []);
            this.dragDepth = 0;
            this.isDragging = false;
            this.dropFolderId = null;
            if (files.length) {
                this.$refs.documentsManagerModal.startUpload(files, folder);
            }
        },

        /**
         * Envoi des fichiers validés dans la fenêtre : affichés en tête de liste avec leur progression,
         * 3 envois en parallèle, puis rafraîchissement de la liste (sans recharger la page)
         */
        startUploads({ files, roles, folder }) {
            const items = files.map(item => ({
                key: ++this.uploadSeq,
                file: item.file,
                name: item.name,
                extension: item.extension,
                size: item.file.size,
                loaded: 0,
                progress: 0,
                status: 'pending',
                error: null,
                documentId: null,
                xhr: null,
                roles: roles,
                folder: folder,
            }));
            this.justAdded = 0;
            this.uploads = this.uploads.concat(items);
            this.processUploads();
        },
        processUploads() {
            const running = this.uploads.filter(item => ['uploading', 'processing'].includes(item.status)).length;
            this.uploads.filter(item => item.status == 'pending')
                .slice(0, Math.max(0, 3 - running))
                .forEach(item => this.uploadFile(item));
        },
        uploadFile(item) {
            const data = new FormData();
            data.append('name', item.name);
            data.append('file', item.file);
            data.append('documentable_id', this.documentableId);
            data.append('documentable_type', this.documentableType);
            data.append('is_folder', 0);
            data.append('document_id', item.folder ? item.folder.id : (this.node ? this.node.id : ''));
            data.append('roles', item.roles);

            const xhr = new XMLHttpRequest();
            xhr.open('POST', this.url);
            const headers = this.getHttpJsonRequestHeaders(true).headers;
            Object.keys(headers).forEach(key => xhr.setRequestHeader(key, headers[key]));

            item.xhr = xhr;
            item.status = 'uploading';
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    item.loaded = Math.min(item.size, e.loaded);
                    item.progress = Math.round(e.loaded / e.total * 100);
                }
            };
            // Fichier transmis : le serveur l'enregistre (stockage, optimisation des images)
            xhr.upload.onload = () => {
                item.status = 'processing';
            };
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    item.status = 'done';
                    item.progress = 100;
                    try { item.documentId = JSON.parse(xhr.responseText).id; } catch (e) { }
                } else {
                    item.status = 'error';
                    item.error = this.uploadErrorMessage(xhr);
                }
                this.onUploadSettled();
            };
            xhr.onerror = () => {
                item.status = 'error';
                item.error = 'Connexion interrompue';
                this.onUploadSettled();
            };
            xhr.onabort = () => {
                this.uploads = this.uploads.filter(upload => upload !== item);
                this.onUploadSettled();
            };
            xhr.send(data);
        },
        onUploadSettled() {
            this.processUploads();
            if (this.uploadingActive) return;

            // Tous les envois sont terminés : la liste est rechargée, les nouveaux fichiers sont mis en évidence
            const done = this.uploads.filter(item => item.status == 'done');
            if (!done.length) return;

            this.getData().then(() => {
                this.uploads = this.uploads.filter(item => item.status != 'done');
                this.highlightIds = done.map(item => item.documentId).filter(id => id);
                this.justAdded = done.length;
                setTimeout(() => {
                    this.highlightIds = [];
                    this.justAdded = 0;
                }, 4000);
            });
        },
        cancelUpload(item) {
            if (item.status == 'uploading' && item.xhr) {
                item.xhr.abort();
            } else {
                this.removeUpload(item);
            }
        },
        retryUpload(item) {
            item.status = 'pending';
            item.error = null;
            item.loaded = 0;
            item.progress = 0;
            this.justAdded = 0;
            this.processUploads();
        },
        removeUpload(item) {
            this.uploads = this.uploads.filter(upload => upload !== item);
        },
        onBeforeUnload(e) {
            e.preventDefault();
            e.returnValue = '';
        },
        uploadErrorMessage(xhr) {
            try {
                const response = JSON.parse(xhr.responseText);
                if (response.errors) {
                    const first = Object.values(response.errors)[0];
                    return Array.isArray(first) ? first[0] : first;
                }
                if (response.message) return response.message;
            } catch (e) { }
            return xhr.status == 413 ? 'La taille de fichier dépasse les 10 Mo' : 'Envoi impossible';
        },

        changeNode(nodeId, isInit = false) {
            this.nodeId = nodeId
            this.search = ''

            if (isInit == false) {
                if (this.nodeId) {
                    history.pushState("", document.title, window.location.pathname + '?documentNode=' + this.nodeId);
                } else {
                    history.pushState("", document.title, window.location.pathname);
                }
            }

            this.getData()
        },
        changeOrder(order) {
            this.order = this.order == order ? ('!' + order) : order
            this.getData()

            if (this.linked) {
                this.getLinkedData()
            }
        },
        getData() {
            this.loading = true;

            return fetch(this.url + "?orderBy=" + this.order + "&documentable_type=" + this.documentableType + "&documentable_id=" + this.documentableId + "&node=" + (this.nodeId ? this.nodeId : ''), {
                ...{
                    method: 'GET'
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                this.loading = false;
                if (response.status == 200) {
                    return response.json().then((data) => {
                        this.folders = data.folders
                        this.documents = data.documents
                        this.breadcrumbs = data.breadcrumbs
                        this.node = data.node
                    })
                }
            }).catch(error => {
                this.loading = false;
                console.log(error)
            });
        },
        /**
         * Documents portés par les éléments liés.
         *
         * État et chargement séparés de la liste propre. Le serveur résout lui-même
         * les éléments liés à partir du seul élément courant, applique les droits,
         * le tri courant et renvoie l'origine (référence + lien) de chaque document.
         */
        async getLinkedData() {
            this.loadingLinked = true;

            fetch(this.url + "?orderBy=" + this.order + "&documentable_type=" + this.documentableType + "&documentable_id=" + this.documentableId + "&linked=1", {
                ...{
                    method: 'GET'
                },
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                this.loadingLinked = false;
                if (response.status == 200) {
                    response.json().then((data) => {
                        this.linkedDocuments = data.documents
                    })
                }
            }).catch(error => {
                this.loadingLinked = false;
                console.log(error)
            });
        },
        /**
         * Suppression d'un document lié : même route DELETE (et donc mêmes
         * contrôles de droits) que pour un document propre. Le document est
         * supprimé de son élément d'origine.
         */
        async deleteLinkedDocument(linkedDocument) {
            const reference = linkedDocument.origin ? linkedDocument.origin.reference : 'un autre élément';

            this.$swal({
                title: "Supprimer ce document ?",
                text: "Ce document appartient à " + reference + ". Il sera supprimé de son élément d'origine.",
                icon: "warning",
                dangerMode: true,
                buttons: ["Annuler", "Supprimer"]
            }).then((willDo) => {
                if (willDo) {
                    this.loading = true;
                    fetch(this.url + "/" + linkedDocument.id, {
                        ...{
                            method: 'DELETE'
                        },
                        ...this.getHttpJsonRequestHeaders()
                    }).then((response) => {
                        if (response.ok) {
                            this.linkedDocuments = this.linkedDocuments.filter(item => item.id !== linkedDocument.id)
                        }
                        this.loading = false;
                    }).catch(error => {
                        this.loading = false;
                        console.log(error);
                    })
                }
            });
        },
        async deleteDocument(document) {
            this.$swal({
                title: "Êtes vous sûr ?",
                text: document.is_folder ? "L'ensemble des documents présents dans le dossier seront également supprimés" : "Le document sera définitivement supprimé",
                icon: "warning",
                dangerMode: true,
                buttons: ["Annuler", "Supprimer"]
            }).then((willDo) => {
                if (willDo) {
                    this.loading = true;
                    fetch(this.url + "/" + document.id, {
                        ...{
                            method: 'DELETE'
                        },
                        ...this.getHttpJsonRequestHeaders()
                    }).then((response) => {
                        if (response.ok) {
                            if (document.is_folder) {
                                this.folders = this.folders.filter(item => item.id !== document.id)
                            } else {
                                this.documents = this.documents.filter(item => item.id !== document.id)
                            }
                        }
                        this.loading = false;
                    }).catch(error => {
                        this.loading = false;
                        console.log(error);
                    })
                }
            });
        },
    },

    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
    },

    async mounted() {
        var node = this.searchInUrl('documentNode');
        this.changeNode(node, true)

        if (this.linked) {
            this.getLinkedData()
        }
    }

}
</script>
